import React, { useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Chip
} from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { AssignmentInd as AssignIcon, NotificationsActive as AlarmIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../services/api';
import { useAuthStore } from '../stores/authStore';
import { userListService } from '../services/userListService';
import { IQuestionnaireExpiredAssignment } from '../types/questionnaire';
import { IUserTreeNode } from '../types/userList';

const formatDate = (value?: string) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString();
};

const Alarm: React.FC = () => {
  const navigate = useNavigate();
  const { user, markAlarmsAsSeen } = useAuthStore();

  useEffect(() => {
    markAlarmsAsSeen();
  }, [markAlarmsAsSeen]);

  const {
    data: assignments = [],
    isLoading: loading,
    error: queryError
  } = useQuery<IQuestionnaireExpiredAssignment[]>({
    queryKey: ['expiredAssignments', user?.id],
    queryFn: async () => {
      const response = await api.get<IQuestionnaireExpiredAssignment[]>('/documents/expired-assignments');
      return response.data;
    },
    enabled: !!user && user.role === 'PLUS',
  });

  const [error, setError] = useState('');
  const [assignNotice, setAssignNotice] = useState('');
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [assignTarget, setAssignTarget] = useState<IQuestionnaireExpiredAssignment | null>(null);
  const [assigneeEmail, setAssigneeEmail] = useState('');
  const [validityDays, setValidityDays] = useState(7);
  const [assignError, setAssignError] = useState('');
  const [assignSubmitting, setAssignSubmitting] = useState(false);
  const [userNodes, setUserNodes] = useState<IUserTreeNode[]>([]);
  const [userNodesLoading, setUserNodesLoading] = useState(false);
  const [userNodesError, setUserNodesError] = useState('');
  const fiveMinutesInDays = 5 / (24 * 60);
  const assignableUsers = useMemo(
    () => userNodes.filter((node) => !node.parentId),
    [userNodes]
  );

  useEffect(() => {
    if (queryError) {
      setError('Failed to load expired assignments.');
    }
  }, [queryError]);

  useEffect(() => {
    const fetchUserNodes = async () => {
      setUserNodesLoading(true);
      try {
        const lists = await userListService.getLists();
        const list = lists[0];
        if (!list) {
          setUserNodes([]);
          return;
        }
        const nodes = await userListService.getNodes(list._id);
        const normalizedNodes = nodes.map((node) => ({
          ...node,
          _id: node._id.toString(),
          listId: node.listId.toString(),
          parentId: node.parentId ? node.parentId.toString() : null
        }));
        setUserNodes(normalizedNodes);
        setUserNodesError('');
      } catch (err: any) {
        setUserNodesError(err.response?.data?.error || 'Failed to load users.');
      } finally {
        setUserNodesLoading(false);
      }
    };

    fetchUserNodes();
  }, []);

  const handleOpenAssignDialog = (assignment: IQuestionnaireExpiredAssignment) => {
    const hasAssignee = assignableUsers.some((node) => node.email === assignment.assigneeEmail);
    setAssignTarget(assignment);
    setAssigneeEmail(hasAssignee ? assignment.assigneeEmail : '');
    setValidityDays(7);
    setAssignError('');
    setAssignNotice('');
    setAssignDialogOpen(true);
  };

  const handleCloseAssignDialog = () => {
    setAssignDialogOpen(false);
    setAssignTarget(null);
  };

  const handleAssign = async () => {
    if (!assignTarget) return;
    if (!assigneeEmail) {
      setAssignError('Please select a user to assign.');
      return;
    }

    setAssignSubmitting(true);
    try {
      await api.post(`/documents/${assignTarget.submissionId}/assign`, {
        assigneeEmail,
        expiresInDays: validityDays
      });
      setAssignDialogOpen(false);
      setAssignNotice(`Assignment email sent to ${assigneeEmail}.`);
      setAssignError('');
    } catch (err: any) {
      setAssignError(err.response?.data?.error || 'Failed to send assignment email.');
    } finally {
      setAssignSubmitting(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AlarmIcon color="error" />
            <Typography variant="h4" sx={{ fontWeight: 600 }}>
              Alarm
            </Typography>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            Expired assigned wills that were not completed.
          </Typography>
        </Box>
        <Button variant="outlined" onClick={() => navigate('/assigned-documents')}>
          Back to Assigned Wills
        </Button>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
      {assignNotice && <Alert severity="success" sx={{ mb: 2 }}>{assignNotice}</Alert>}

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Title</TableCell>
              <TableCell>Assigned To</TableCell>
              <TableCell>Assigned</TableCell>
              <TableCell>Expired</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {assignments.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">
                  No expired assignments.
                </TableCell>
              </TableRow>
            ) : (
              assignments.map((assignment) => (
                <TableRow key={assignment.assignmentId} hover>
                  <TableCell sx={{ fontWeight: 500 }}>
                    <Box sx={{ display: 'grid', gap: 0.5 }}>
                      <Typography variant="body1" sx={{ fontWeight: 600 }}>
                        {assignment.templateTitle}
                      </Typography>
                      {assignment.templateDescription && (
                        <Typography variant="body2" color="text.secondary">
                          {assignment.templateDescription}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>{assignment.assigneeEmail}</TableCell>
                  <TableCell>{formatDate(assignment.assignedAt)}</TableCell>
                  <TableCell>{formatDate(assignment.expiresAt)}</TableCell>
                  <TableCell>
                    <Chip label="Expired" color="error" size="small" />
                  </TableCell>
                  <TableCell align="right">
                    <Button
                      variant="outlined"
                      size="small"
                      startIcon={<AssignIcon />}
                      onClick={() => handleOpenAssignDialog(assignment)}
                    >
                      Assign
                    </Button>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={assignDialogOpen} onClose={handleCloseAssignDialog} maxWidth="xs" fullWidth>
        <DialogTitle>Assign Will</DialogTitle>
        <DialogContent dividers>
          {assignError && <Alert severity="error" sx={{ mb: 2 }}>{assignError}</Alert>}
          {userNodesError && <Alert severity="error" sx={{ mb: 2 }}>{userNodesError}</Alert>}
          <FormControl fullWidth sx={{ mb: 3 }} disabled={userNodesLoading || assignableUsers.length === 0}>
            <InputLabel id="assign-user-label">User</InputLabel>
            <Select
              labelId="assign-user-label"
              value={assigneeEmail}
              label="User"
              onChange={(event) => setAssigneeEmail(event.target.value as string)}
            >
              {assignableUsers.map((node) => (
                <MenuItem key={node._id} value={node.email}>
                  {node.fullName} ({node.email})
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          {assignableUsers.length === 0 && !userNodesLoading && (
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No users found in your list. Add users in the Users page first.
            </Typography>
          )}
          <FormControl fullWidth>
            <InputLabel id="assign-validity-label">Valid for</InputLabel>
            <Select
              labelId="assign-validity-label"
              value={String(validityDays)}
              label="Valid for"
              onChange={(event) => setValidityDays(Number(event.target.value))}
            >
              <MenuItem value={String(fiveMinutesInDays)}>5 minutes</MenuItem>
              {Array.from({ length: 30 }, (_, index) => index + 1).map((days) => (
                <MenuItem key={days} value={String(days)}>
                  {days} {days === 1 ? 'day' : 'days'}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAssignDialog}>Cancel</Button>
          <Button variant="contained" onClick={handleAssign} disabled={assignSubmitting || !assigneeEmail}>
            {assignSubmitting ? 'Sending...' : 'Send Assignment'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Alarm;
